import { useEffect, useId, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatList } from '@/i18n/intl';
import { cn } from '@/lib/utils';
import { useCopy } from '@/hooks/useCopy';
import { useToast } from '@/components/ui/use-toast';
import { StatusChip, MONO_LABEL_TYPE } from '@/components/editorial';
import { Progress } from '@/components/ui/progress';
import { Upload, FileCheck, FileText, Image as ImageIcon } from 'lucide-react';
import { MAX_UPLOAD_MB, UPLOAD_ACCEPT, isEmptyUpload, uploadFailureText, uploadRefusal } from '@/lib/uploadTypes';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

export interface DocSlot {
  kind: string;
  label: string;
  hint: string;
  phi?: boolean;
  /** When true, this slot accepts several distinct documents (F-8). */
  multiple?: boolean;
  /** Upper bound on documents for a `multiple` slot (F-8 default: 5). */
  maxCount?: number;
  /** #2197: the family must fill this slot before submitting (staff setting,
   *  GET /api/applications/document-requirements; the server enforces it). */
  required?: boolean;
}

/** One persisted upload as the tiles render it. */
interface UploadedFile {
  /** Absent on rows that only name a file (the copy editor's preview). */
  id?: string;
  filename: string;
  staff: boolean;
  /** #2471: who added it — only the family's own uploads are removable. */
  provenance?: string;
  mime?: string;
  size?: number;
}

/** The persisted row as the wizard holds it. */
export interface UploadedDocument {
  id?: string;
  kind: string;
  filename: string;
  uploaded_by_staff?: boolean;
  provenance?: string;
  mime_type?: string;
  file_size?: number;
}

/** #2471: a name as a parent reads it — macOS puts U+202F before "AM" in
 *  screenshot names, which some fonts draw as a box. */
const displayName = (name: string) => name.normalize('NFC').replace(/[\u00A0\u202F\u2007]/g, ' ');

/** "340 KB", "1.2 MB" — the unit is a symbol, the number follows the locale. */
function formatSize(bytes: number | undefined, locale: string): string {
  if (!bytes) return '';
  const nf = new Intl.NumberFormat(locale, { maximumFractionDigits: 1 });
  return bytes >= 1024 * 1024 ? `${nf.format(bytes / (1024 * 1024))} MB` : `${nf.format(Math.max(1, Math.round(bytes / 1024)))} KB`;
}

/** #2473: a phone or tablet, where the picker opens the photo library first.
 *  `pointer: coarse` is the primary input, so a laptop with a touchscreen keeps
 *  "Choose file". Read once per tile: the input device does not change mid-step. */
function isTouchDevice(): boolean {
  return typeof window !== 'undefined'
    && typeof window.matchMedia === 'function'
    && window.matchMedia('(pointer: coarse)').matches;
}

/** Does this drag carry files from the operating system (Finder, Photos)? */
const isFileDrag = (e: { dataTransfer: DataTransfer | null }) =>
  Array.from(e.dataTransfer?.types ?? []).includes('Files');

/** The files a drop carries. `items` first: a drag from macOS Photos arrives
 *  as a file promise that some browsers expose only there. */
function droppedFiles(dt: DataTransfer): File[] {
  const fromItems = Array.from(dt.items ?? [])
    .filter((item) => item.kind === 'file')
    .map((item) => item.getAsFile())
    .filter((f): f is File => f !== null);
  return fromItems.length > 0 ? fromItems : Array.from(dt.files ?? []);
}

/** A small preview of an image the family uploaded; an icon for anything else
 *  or while it loads. The blob URL lives only as long as the row. */
function FileThumb({ file, load }: { file: UploadedFile; load?: (file: UploadedFile) => Promise<Blob> }) {
  const [url, setUrl] = useState<string | null>(null);
  const isImage = !!file.mime?.startsWith('image/');
  useEffect(() => {
    if (!load || !file.id || !isImage || typeof URL.createObjectURL !== 'function') return undefined;
    let alive = true;
    let made: string | null = null;
    load(file).then((blob) => {
      if (!alive) return;
      made = URL.createObjectURL(blob);
      setUrl(made);
    }).catch(() => { /* the icon stays — a thumbnail is a nicety */ });
    return () => {
      alive = false;
      if (made) URL.revokeObjectURL(made);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- one fetch per document id
  }, [file.id]);
  return (
    <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-md bg-muted text-stevens-ink-3">
      {url ? (
        <img src={url} alt="" className="h-full w-full object-cover" />
      ) : isImage ? (
        <ImageIcon size={18} aria-hidden />
      ) : (
        <FileText size={18} aria-hidden />
      )}
    </span>
  );
}

interface Props<D extends UploadedDocument = UploadedDocument> {
  slots: DocSlot[];
  // #1005: `uploaded_by_staff` rides along so a document TRF attached on the
  // family's behalf can say so here too. Optional — rows written before the
  // field existed omit it, and only `=== true` is a claim.
  uploaded: D[];
  // #1425: `opts.replace` is set ONLY by the single-slot confirm path below.
  // A single-slot kind is capped at 1 server-side (#1386/#1406), so a confirmed
  // Replace that re-POSTs without saying it is a replace comes back 400 and the
  // family sees a destructive toast instead of the swap they just agreed to.
  // Optional and confirm-scoped on purpose: the flag is the user's consent to
  // retire the prior document, so an unconfirmed upload must never carry it.
  onUpload: (kind: string, file: File, opts?: UploadOptions) => Promise<UploadOutcome | void> | UploadOutcome | void;
  /** #2471: open one of the listed files. Absent: names render as text. */
  onView?: (doc: D) => void;
  /** #2471: remove one of the family's own uploads. Absent: no Remove. */
  onRemove?: (doc: D) => void;
  /** #2471: the bytes of a listed image, for its thumbnail. */
  loadFile?: (doc: D) => Promise<Blob>;
  /**
   * #2377: the required kinds the wizard just refused to leave the step
   * without. Each one still empty says so at its own tile, and
   * focus moves to the first of them in tile order. A new array means a new
   * refusal, so the wizard sets it only when it blocks.
   */
  missingKinds?: string[];
  /** #2752: the /renew walk. The note under the heading says "your renewal"
   *  once the reader's language has that line; until then it keeps the
   *  language's own application wording rather than switching to English. */
  renewal?: boolean;
}

/** #2472: the two photo slots, whose touch control opens the camera roll. */
const PHOTO_KINDS: ReadonlySet<string> = new Set(['child_photo', 'family_photo']);

/** Extra intent that rides along with one upload (#1425). */
export interface UploadOptions {
  /** This upload REPLACES the single-slot kind's existing document. */
  replace?: boolean;
  /** #2477: 0–100 as the bytes go up, for the row's progress bar. */
  onProgress?: (percent: number) => void;
}

/** #2477: how one upload ended. `void` (an older or inert caller) reads as ok. */
export type UploadOutcome = { ok: true } | { ok: false; error: string };

/** One file on its way up, or one that failed and can be retried. */
interface PendingUpload {
  key: string;
  file: File;
  replace?: boolean;
  progress: number;
  error?: string;
}

function UploadTile({
  slot,
  uploadedFiles,
  onUpload,
  addedByTrf,
  capToastTitle,
  onView,
  onRemove,
  loadFile,
  announce,
  missing,
  buttonRef,
}: {
  slot: DocSlot;
  uploadedFiles: UploadedFile[];
  onUpload: (kind: string, file: File, opts?: UploadOptions) => Promise<UploadOutcome | void> | UploadOutcome | void;
  addedByTrf: string;
  capToastTitle: string;
  onView?: (file: UploadedFile) => void;
  onRemove?: (file: UploadedFile) => void;
  loadFile?: (file: UploadedFile) => Promise<Blob>;
  /** #2477: say something once through the step's one live region. */
  announce: (message: string) => void;
  /** #2377: the wizard refused to continue while this required slot was short. */
  missing: boolean;
  buttonRef: (el: HTMLButtonElement | null) => void;
}) {
  const { t, i18n } = useTranslation(['forms', 'common']);
  // #2470: nested dragenter/dragleave pairs fire as the pointer crosses the
  // tile's children, so the highlight follows a depth count, not a boolean.
  const [dragDepth, setDragDepth] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const { toast } = useToast();
  // F-2 single-slot replace confirmation: the incoming file that would
  // overwrite the existing upload, held until the user confirms via
  // AlertDialog. `null` means no pending replacement (dialog closed).
  const [pendingFile, setPendingFile] = useState<File | null>(null);

  // #2477: uploads in flight (and failed ones awaiting Retry) are rows too.
  const [pending, setPending] = useState<PendingUpload[]>([]);
  const listRef = useRef<HTMLUListElement>(null);
  const uploadButtonRef = useRef<HTMLButtonElement | null>(null);
  // Where focus goes once a removed row has left the list.
  const focusAfterRemove = useRef<number | null>(null);
  // #2377: the last file this tile refused before any request (wrong type, too
  // large), named beside the tile until the next pick or drop. A refusal from
  // the server is the failed row's own line (#2477), with Retry. COPY IS
  // DRAFT: family-facing wording is Melanie's to approve.
  const [refusal, setRefusal] = useState<string | null>(null);
  const errorId = `${useId()}-error`;
  // #2473: on a phone the control says what the picker will open. No
  // `capture` on the input, so the library stays reachable, not only the camera.
  const [touch] = useState(isTouchDevice);

  /** The sentence for a file the server would refuse, or null. */
  const refusalFor = (file: File): string | null => {
    const why = uploadRefusal(file);
    if (why === 'type') return t('forms:documents.refusedType', { file: displayName(file.name) });
    if (why === 'size') return t('forms:documents.refusedSize', { file: displayName(file.name), max: MAX_UPLOAD_MB });
    if (isEmptyUpload(file)) return t('forms:documents.refusedEmpty', { file: displayName(file.name) });
    return null;
  };

  const isMultiple = !!slot.multiple;
  const maxCount = isMultiple ? slot.maxCount ?? 5 : 1;
  const inFlight = pending.filter((p) => !p.error).length;
  const atMax = isMultiple && uploadedFiles.length + inFlight >= maxCount;

  const patchPending = (key: string, patch: Partial<PendingUpload>) =>
    setPending((list) => list.map((p) => (p.key === key ? { ...p, ...patch } : p)));

  const run = async (key: string, file: File, replace?: boolean) => {
    const name = displayName(file.name);
    patchPending(key, { progress: 0, error: undefined });
    announce(t('forms:documents.uploadingFile', { file: name }));
    let outcome: UploadOutcome | void;
    try {
      outcome = await onUpload(slot.kind, file, {
        ...(replace ? { replace: true } : {}),
        onProgress: (progress) => patchPending(key, { progress }),
      });
    } catch (err) {
      // #2473: a thrown failure is worded by kind (too large, wrong type,
      // network, failed), never the transport's own text.
      outcome = { ok: false, error: uploadFailureText(t, err, i18n.language ?? 'en') };
    }
    if (outcome && outcome.ok === false) {
      patchPending(key, { error: outcome.error || t('forms:documents.uploadFailedRow') });
      announce(t('forms:documents.uploadFailedFile', { file: name }));
      return;
    }
    setPending((list) => list.filter((p) => p.key !== key));
    announce(t('forms:documents.uploadedFile', { file: name }));
  };

  const start = (file: File, replace?: boolean) => {
    const key = `${file.name}-${file.size}-${Math.random().toString(36).slice(2)}`;
    setPending((list) => [...list, { key, file, replace, progress: 0 }]);
    void run(key, file, replace);
  };

  const removeRow = (f: UploadedFile, index: number) => {
    focusAfterRemove.current = index;
    onRemove!(f);
  };

  // #2477: after a row leaves, focus its neighbour — the next row, else the
  // previous, else the tile's own upload button — never the page top.
  useEffect(() => {
    const index = focusAfterRemove.current;
    if (index === null) return;
    focusAfterRemove.current = null;
    const rows = listRef.current?.querySelectorAll<HTMLElement>('[data-row-focus]') ?? [];
    const target = rows[Math.min(index, rows.length - 1)] ?? uploadButtonRef.current;
    target?.focus();
  }, [uploadedFiles.length]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files ?? []);
    // Reset immediately so the same file can be re-selected if needed.
    e.target.value = '';
    acceptFiles(files);
  };

  /** One path for picked AND dropped files (#2470), so caps, the replace
   *  confirmation and the server's checks apply the same way to both. */
  const acceptFiles = (incoming: File[]) => {
    if (incoming.length === 0) return;

    // #2377: a file the server would refuse is named at the tile before any
    // request, before the replace dialog could offer to swap it in, and takes
    // no room under the cap. Each new pick or drop replaces the line.
    const refusals = incoming.map(refusalFor);
    const firstRefusal = refusals.find((r) => r !== null) ?? null;
    setRefusal(firstRefusal);
    if (firstRefusal) announce(firstRefusal);
    const files = incoming.filter((_, i) => refusals[i] === null);
    if (files.length === 0) return;

    if (!isMultiple) {
      // Single-slot kind. F-2: a replace must never silently drop the prior
      // upload — require an explicit blocking confirmation that names the
      // file being replaced. This is the "slot full" path. #353: blocking
      // confirmation is a shadcn/Radix AlertDialog (focus-trapped, consistent
      // screen-reader behavior) rather than native `window.confirm`.
      const file = files[0];
      if (uploadedFiles.length > 0) {
        setPendingFile(file);
        return;
      }
      start(file);
      return;
    }

    // Multiple-slot kind (F-8): accumulate, respecting the cap. A selection
    // that overflows the remaining room used to slice silently (#1327) —
    // upload what fits and name what didn't so nothing vanishes unexplained.
    const remaining = Math.max(0, maxCount - uploadedFiles.length - inFlight);
    const kept = files.slice(0, remaining);
    const dropped = files.slice(remaining);
    kept.forEach((f) => start(f));
    if (dropped.length > 0) {
      toast({
        variant: 'destructive',
        title: capToastTitle,
        // The body is interpolated (room left, slot, filenames): one count key
        // (#2109 R4-B29). The file names are a unit list, so en keeps "a, b".
        // COPY IS DRAFT: family-facing wording is Melanie's to approve.
        description: t('forms:documents.capOverflow', {
          count: remaining,
          slot: slot.label,
          files: formatList(dropped.map((f) => f.name), { type: 'unit', style: 'short' }),
        }),
      });
    }
  };

  const confirmReplace = () => {
    // #1425: the confirmation is what makes this a replace, so it is what
    // carries the flag. The server supersedes the document already in the slot
    // only after the replacement is stored, so a failed upload still leaves the
    // family with the file they had.
    if (pendingFile) start(pendingFile, true);
    setPendingFile(null);
  };

  const hasFiles = uploadedFiles.length > 0 || pending.length > 0;
  const currentFilename = uploadedFiles[0]?.filename;
  const dragging = dragDepth > 0;
  // #2377: a refusal outranks the missing line; a filled slot is not missing.
  // #2472: one file fills any slot. The photos are two slots, so the photo
  // tile no longer needs a "needs at least 2" line.
  const problem =
    refusal ?? (missing && uploadedFiles.length === 0 ? t('forms:documents.missingTile') : null);

  // #2470: the whole tile is a drop target. `preventDefault` on dragover is
  // what makes the browser fire `drop` at all; without it a dropped file was
  // ignored, or opened in place of the application.
  const onDragEnter = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    setDragDepth((d) => d + 1);
  };
  const onDragOver = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = atMax ? 'none' : 'copy';
  };
  const onDragLeave = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    setDragDepth((d) => Math.max(0, d - 1));
  };
  const onDrop = (e: React.DragEvent) => {
    if (!isFileDrag(e)) return;
    e.preventDefault();
    setDragDepth(0);
    if (atMax) {
      toast({ variant: 'destructive', title: capToastTitle, description: t('forms:documents.maxReached', { count: maxCount }) });
      return;
    }
    const files = droppedFiles(e.dataTransfer);
    if (files.length === 0) {
      // A drag from macOS Photos can arrive as a promise the browser never
      // resolves. Say so — a silent no-op reads as "it worked".
      toast({ variant: 'destructive', title: t('forms:documents.dropEmptyTitle'), description: t('forms:documents.dropEmptyBody') });
      return;
    }
    acceptFiles(files);
  };

  return (
    <div
      className="space-y-2"
      data-drop-zone
      data-testid={`doc-drop-${slot.kind}`}
      onDragEnter={onDragEnter}
      onDragOver={onDragOver}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
    >
      <button
        ref={(el) => {
          uploadButtonRef.current = el;
          buttonRef(el);
        }}
        type="button"
        onClick={() => inputRef.current?.click()}
        aria-describedby={problem ? errorId : undefined}
        disabled={atMax}
        className={cn(
          'w-full rounded-xl border-2 border-dashed border-border hover:border-stevens-indigo/50 disabled:opacity-50 disabled:hover:border-border disabled:cursor-not-allowed bg-background hover:bg-stevens-indigo/5 transition-colors p-5 text-start flex items-start gap-4',
          dragging && 'border-stevens-indigo bg-stevens-indigo/10',
        )}
      >
        <div className="shrink-0 mt-1 text-stevens-ink-3">
          {hasFiles ? (
            <FileCheck size={20} className="text-stevens-indigo" aria-hidden />
          ) : (
            <Upload size={20} aria-hidden />
          )}
        </div>

        <div className="flex-1 min-w-0 space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              className="font-serif text-body font-medium text-foreground"
            >
              {slot.label}
            </span>
            {/* #2197: a mandatory slot says so in place of the "optional"
                chip — waiting (warn) while empty, satisfied (ok) once filled.
                A PHI slot that is not required keeps its PHI chip alone, as
                before. */}
            {slot.required ? (
              <StatusChip
                tone={uploadedFiles.length > 0 ? 'ok' : 'warn'}
                data-testid={`doc-required-${slot.kind}`}
              >
                {t('forms:documents.requiredChip')}
              </StatusChip>
            ) : (
              <StatusChip tone="neutral">
                {t('forms:documents.optionalChip')}
              </StatusChip>
            )}
            {isMultiple && (
              <StatusChip tone="neutral">
                {uploadedFiles.length}/{maxCount}
              </StatusChip>
            )}
          </div>
          <p className="text-caption text-stevens-ink-3 leading-snug">
            {dragging
              ? t('forms:documents.dropHere')
              : atMax ? t('forms:documents.maxReached', { count: maxCount }) : slot.hint}
          </p>
          {/* #2473: the rules a file must meet, before the family picks one,
              and what the control opens. */}
          {!atMax && !dragging && (
            <p className="text-caption text-stevens-ink-3" data-testid={`doc-rules-${slot.kind}`}>
              {t('forms:documents.fileRules', { max: MAX_UPLOAD_MB })}
            </p>
          )}
          {!atMax && !dragging && (
            <p className="text-caption font-medium text-stevens-indigo" data-testid={`doc-choose-${slot.kind}`}>
              {touch
                ? t(PHOTO_KINDS.has(slot.kind) ? 'forms:documents.choosePhoto' : 'forms:documents.choosePhotoOrFile')
                : t('forms:documents.chooseFile')}
            </p>
          )}
          {!atMax && !dragging && !touch && (
            <p className="hidden text-caption text-stevens-ink-3 md:block">{t('forms:documents.dropHint')}</p>
          )}
        </div>
      </button>

      {/* Hidden file input */}
      <input
        ref={inputRef}
        type="file"
        accept={UPLOAD_ACCEPT}
        multiple={isMultiple}
        className="sr-only"
        aria-label={t('forms:documents.uploadLabel', { slot: slot.label })}
        aria-invalid={problem ? true : undefined}
        aria-describedby={problem ? errorId : undefined}
        onChange={handleChange}
      />

      {/* #2377: the refusal or the missing line, at the tile it belongs to.
          Not a live region of its own (#2477: one per step): a refusal is
          announced through the step's region, and a missing tile takes focus,
          which reads this line through aria-describedby. */}
      {problem && (
        <p id={errorId} data-testid={`doc-problem-${slot.kind}`} className="text-caption text-status-danger-fg">
          {problem}
        </p>
      )}

      {/* #2471: the roster — every persisted document, each one openable,
          and the family's own uploads removable while the record is a draft.
          #1005: a file the family never sent says where it came from. */}
      {hasFiles && (
        <ul ref={listRef} className="space-y-2" aria-label={slot.label}>
          {uploadedFiles.map((f, i) => {
            const name = displayName(f.filename || '');
            const removable = !!onRemove && !!f.id && !f.staff && (f.provenance ?? 'family') === 'family';
            const meta = [
              formatSize(f.size, i18n.language),
              f.staff ? addedByTrf : f.provenance === 'provider' ? t('forms:documents.addedByCareTeam') : '',
            ].filter(Boolean).join(' · ');
            return (
              <li
                key={f.id ?? `${f.filename}-${i}`}
                className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2"
              >
                <FileThumb file={f} load={loadFile} />
                <div className="min-w-0 flex-1">
                  {onView && f.id ? (
                    <button
                      type="button"
                      data-row-focus
                      onClick={() => onView(f)}
                      className="block max-w-full truncate text-start text-body text-stevens-indigo underline-offset-2 hover:underline"
                      title={t('forms:documents.openFile', { file: name })}
                    >
                      {name}
                    </button>
                  ) : (
                    <span className="block truncate text-body text-foreground">{name}</span>
                  )}
                  {meta && <p className="text-caption text-stevens-ink-3">{meta}</p>}
                </div>
                {removable && (
                  <button
                    type="button"
                    onClick={() => removeRow(f, i)}
                    aria-label={t('forms:documents.removeFile', { file: name })}
                    className="shrink-0 rounded-md px-2 py-1 text-caption text-stevens-ink-2 hover:bg-muted hover:text-status-danger-fg"
                  >
                    {t('forms:documents.remove')}
                  </button>
                )}
              </li>
            );
          })}
          {pending.map((p) => {
            const name = displayName(p.file.name);
            return (
              <li
                key={p.key}
                data-testid="doc-pending"
                className="flex items-center gap-3 rounded-lg border border-border bg-background px-3 py-2"
              >
                <FileThumb file={{ filename: p.file.name, staff: false, mime: p.file.type }} />
                <div className="min-w-0 flex-1">
                  <span className="block truncate text-body text-foreground">{name}</span>
                  {p.error ? (
                    <p className="text-caption text-status-danger-fg">{p.error}</p>
                  ) : (
                    <Progress
                      value={p.progress}
                      className="mt-1 h-1.5"
                      aria-label={t('forms:documents.uploadingFile', { file: name })}
                    />
                  )}
                </div>
                {p.error && (
                  <div className="flex shrink-0 gap-1">
                    <button
                      type="button"
                      onClick={() => void run(p.key, p.file, p.replace)}
                      aria-label={t('forms:documents.retryFile', { file: name })}
                      className="rounded-md px-2 py-1 text-caption text-stevens-indigo hover:bg-muted"
                    >
                      {t('forms:documents.retry')}
                    </button>
                    <button
                      type="button"
                      onClick={() => setPending((list) => list.filter((x) => x.key !== p.key))}
                      aria-label={t('forms:documents.dismissFile', { file: name })}
                      className="rounded-md px-2 py-1 text-caption text-stevens-ink-2 hover:bg-muted"
                    >
                      {t('forms:documents.dismiss')}
                    </button>
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {/* F-2/#353: blocking replace confirmation, shadcn AlertDialog (not
          window.confirm) — focus-trapped, consistent screen-reader behavior.
          Controlled by `pendingFile`; closing without confirming (Cancel,
          Escape, overlay click) clears it so the prior upload is untouched. */}
      <AlertDialog
        open={pendingFile !== null}
        onOpenChange={(open) => {
          if (!open) setPendingFile(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('forms:documents.replaceTitle', { file: currentFilename })}</AlertDialogTitle>
            <AlertDialogDescription>
              {t('forms:documents.replaceBody', { file: currentFilename, next: pendingFile?.name ?? '' })}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmReplace}>{t('forms:documents.replace')}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

export function DocumentUploadStep<D extends UploadedDocument>({ slots, uploaded, onUpload, onView, onRemove, loadFile, missingKinds, renewal = false }: Props<D>) {
  // #1005 / D-CS-2: family-facing prose is a registered slot, not a literal —
  // TRF rewords it without a developer. Read ONCE here rather than per tile:
  // the tiles are a loop, and a network-backed hook inside one is a read per
  // slot for a single sentence.
  const { t, i18n } = useTranslation(['forms', 'common']);
  const { copy, ready } = useCopy();
  /** #2752: the renewal line when this is a renewal and the language has it. */
  const renewalLine = (key: 'documents.allRequiredNoteRenewal' | 'documents.skipNoteSomeRequiredRenewal'): boolean => {
    if (!renewal) return false;
    const lng = i18n.resolvedLanguage ?? i18n.language ?? 'en';
    if (lng === 'en' || lng.startsWith('en-')) return true;
    const value: unknown = i18n.getResource(lng, 'forms', key);
    return typeof value === 'string' && value.trim() !== '';
  };
  const addedByTrf = copy('apply.documents.added_by_trf');
  const capToastTitle = copy('apply.documents.cap_toast_title');
  // Group persisted filenames by kind so a `multiple` slot can list every one
  // of its documents (F-8) rather than collapsing them to a single entry.
  const uploadedByKind = uploaded.reduce<Record<string, UploadedFile[]>>((acc, u) => {
    (acc[u.kind] ??= []).push({
      id: u.id,
      filename: u.filename,
      staff: u.uploaded_by_staff === true,
      provenance: u.provenance,
      mime: u.mime_type,
      size: u.file_size,
    });
    return acc;
  }, {});
  // #2477: ONE polite live region for the whole step — upload started, done,
  // failed. Per-row regions would talk over each other.
  const [liveMessage, setLiveMessage] = useState('');
  const byId = new Map(uploaded.filter((u) => u.id).map((u) => [u.id!, u]));
  const toDoc = (f: UploadedFile) => byId.get(f.id ?? '')!;

  // #2377: move focus to the first still-short tile the wizard refused on, in
  // tile order. Keyed on the array the wizard sets per refusal and on `ready`,
  // because the tiles do not exist until the copy hold below lets go.
  const tileButtons = useRef(new Map<string, HTMLButtonElement>());
  const uploadedRef = useRef(uploadedByKind);
  uploadedRef.current = uploadedByKind;
  useEffect(() => {
    if (!ready || !missingKinds?.length) return;
    const first = slots.find(
      (s) => missingKinds.includes(s.kind) && (uploadedRef.current[s.kind]?.length ?? 0) === 0,
    );
    if (!first) return;
    const button = tileButtons.current.get(first.kind);
    button?.focus();
    // Optional-call: jsdom does not implement scrollIntoView.
    button?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
  }, [missingKinds, ready, slots]);

  // #2470: a file dropped anywhere but a tile must not navigate the browser to
  // it — that would replace the application with the file. Cancel those drops
  // (and show "no drop" there) for the life of the step.
  useEffect(() => {
    const outside = (e: DragEvent) => isFileDrag(e)
      && !(e.target instanceof Element && e.target.closest('[data-drop-zone]'));
    const onOver = (e: DragEvent) => {
      if (!outside(e)) return;
      e.preventDefault();
      if (e.dataTransfer) e.dataTransfer.dropEffect = 'none';
    };
    const onDropOutside = (e: DragEvent) => {
      if (outside(e)) e.preventDefault();
    };
    window.addEventListener('dragover', onOver);
    window.addEventListener('drop', onDropOutside);
    return () => {
      window.removeEventListener('dragover', onOver);
      window.removeEventListener('drop', onDropOutside);
    };
  }, []);

  // Guardrail 7: hold the paint until the copy read has SETTLED — succeeded or
  // failed, either is an answer and neither takes more than one attempt. #1114
  // gave the review step this hold and left it off here, so the wizard held
  // correctly on the review screen and flickered TRF's published wording in
  // over the seed on the screen immediately before it (#1542). This is also
  // the step whose read warms the query key the review step then reuses, so
  // holding here is what makes that step's "already warmed" note true.
  if (!ready) {
    return (
      <div
        className="py-6"
        role="status"
        aria-live="polite"
        data-testid="document-upload-step-loading"
      >
        <span className={cn(MONO_LABEL_TYPE, 'block font-normal')}>{t('common:states.loading')}</span>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="document-upload-step">
      <div className="sr-only" role="status" aria-live="polite" data-testid="doc-live-region" data-live-announcer>{liveMessage}</div>
      {/* Section header */}
      <div className="space-y-1">
        <span className={cn(MONO_LABEL_TYPE, 'text-stevens-indigo')}>
          {t('forms:documents.eyebrow')}
        </span>
        <h2
          className="font-serif text-title font-normal leading-snug text-foreground m-0"
        >
          {t('forms:documents.title')}
        </h2>
      </div>

      {/* Skip note. #2197: "All optional" is only true when nothing is
          required; otherwise the note says which ones cannot be skipped. */}
      <p className="text-body text-stevens-ink-3 italic">
        {slots.length > 0 && slots.every((s) => s.required)
          ? t(renewalLine('documents.allRequiredNoteRenewal') ? 'forms:documents.allRequiredNoteRenewal' : 'forms:documents.allRequiredNote')
          : slots.some((s) => s.required)
            ? t(renewalLine('documents.skipNoteSomeRequiredRenewal') ? 'forms:documents.skipNoteSomeRequiredRenewal' : 'forms:documents.skipNoteSomeRequired')
            : t('forms:documents.skipNote')}
      </p>

      {/* Upload tiles */}
      <div className="space-y-4">
        {slots.map((slot) => (
          <UploadTile
            key={slot.kind}
            slot={slot}
            uploadedFiles={uploadedByKind[slot.kind] ?? []}
            onUpload={onUpload}
            addedByTrf={addedByTrf}
            capToastTitle={capToastTitle}
            onView={onView ? (f) => onView(toDoc(f)) : undefined}
            onRemove={onRemove ? (f) => onRemove(toDoc(f)) : undefined}
            loadFile={loadFile ? (f) => loadFile(toDoc(f)) : undefined}
            announce={setLiveMessage}
            missing={missingKinds?.includes(slot.kind) ?? false}
            buttonRef={(el) => {
              if (el) tileButtons.current.set(slot.kind, el);
              else tileButtons.current.delete(slot.kind);
            }}
          />
        ))}
      </div>
    </div>
  );
}
